import React from 'react';
import { NavLink, Outlet, useLoaderData } from 'react-router-dom';
import { Await } from '@oysterjs/uiv2/async';

import clsx from 'clsx';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  TransitionChild
} from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  RectangleStackIcon,
  SparklesIcon,
  TicketIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { CaraWordMark } from '@oysterjs/uiv2/logo';
import { AgencyUserFieldsFragment } from '../api/graphql';

const navigationGroups = [
  {
    pages: [{ name: 'Home', href: 'home', icon: HomeIcon }]
  },
  {
    name: 'Copilot',
    pages: [
      { name: 'Workspaces', href: 'workspaces', icon: SparklesIcon },
      { name: 'Presets', href: 'presets', icon: CubeIcon },
      { name: 'Styles', href: 'styles', icon: DocumentChartBarIcon }
    ]
  },
  {
    name: 'Servicing',
    pages: [{ name: 'Service Requests', href: 'servicing', icon: TicketIcon }]
  },
  {
    name: 'Communication',
    pages: [
      { name: 'Calls', href: 'calls', icon: PhoneIcon },
      { name: 'Emails', href: 'emails', icon: EnvelopeIcon }
    ]
  },
  {
    name: 'Knowledge',
    pages: [
      { name: 'Knowledge Base', href: 'knowledge', icon: CircleStackIcon },
      { name: 'Accounts', href: 'accounts', icon: RectangleStackIcon },
      { name: 'Policies', href: 'policies', icon: DocumentTextIcon }
    ]
  }
];

export const DashboardContainer = () => {
  const loader = useLoaderData() as { data: Promise<{ agencyUser: AgencyUserFieldsFragment }> };
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <React.Suspense fallback={<DashboardContainerLoader />}>
      <Await resolve={loader.data}>
        {({ agencyUser }) => {
          return (
            <>
              <Dialog
                open={sidebarOpen}
                onClose={setSidebarOpen}
                className="relative z-50 lg:hidden"
              >
                <DialogBackdrop
                  transition
                  className="fixed inset-0 bg-neutral-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 flex">
                  <DialogPanel
                    transition
                    className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                  >
                    <TransitionChild>
                      <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                        <button
                          type="button"
                          onClick={() => setSidebarOpen(false)}
                          className="-m-2.5 p-2.5"
                        >
                          <span className="sr-only">Close sidebar</span>
                          <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                        </button>
                      </div>
                    </TransitionChild>
                    {/* Sidebar component, swap this element with another sidebar if you like */}
                    <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-8 pb-4">
                      <div className="flex py-6 mt-1 shrink-0 items-center">
                        <CaraWordMark scale={0.5} />
                      </div>
                      <nav className="flex flex-1 flex-col">
                        <ul role="list" className="flex flex-1 flex-col gap-y-7">
                          <li>
                            <ul role="list" className="-mx-2">
                              {navigationGroups.map((group) => (
                                <>
                                  {group.name && (
                                    <li
                                      key={group.name}
                                      className="text-xs uppercase font-semibold leading-6 text-neutral-400 mt-6 mx-4 -mb-0.5 first:mt-0"
                                    >
                                      {group.name}
                                    </li>
                                  )}
                                  {group.pages.map((page) => (
                                    <li key={page.name} className="mt-1.5 first:mt-0">
                                      <NavLink
                                        to={page.href}
                                        className={({ isActive }) =>
                                          clsx(
                                            isActive
                                              ? 'bg-primary-50 text-primary-600'
                                              : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary-600',
                                            'group flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6'
                                          )
                                        }
                                      >
                                        {({ isActive }) => (
                                          <>
                                            <page.icon
                                              aria-hidden="true"
                                              className={clsx(
                                                isActive
                                                  ? 'text-primary-600'
                                                  : 'text-neutral-400 group-hover:text-primary-600',
                                                'h-6 w-6 shrink-0'
                                              )}
                                            />
                                            {page.name}
                                          </>
                                        )}
                                      </NavLink>
                                    </li>
                                  ))}
                                </>
                              ))}
                            </ul>
                          </li>
                          <li className="mt-auto hidden">
                            <NavLink
                              to="#"
                              className="group -mx-2 flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6 text-neutral-700 hover:bg-neutral-50 hover:text-primary-600"
                            >
                              <Cog6ToothIcon
                                aria-hidden="true"
                                className="h-6 w-6 shrink-0 text-neutral-400 group-hover:text-primary-600"
                              />
                              Settings
                            </NavLink>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </DialogPanel>
                </div>
              </Dialog>

              {/* Static sidebar for desktop */}
              <div className="hidden lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:w-64 lg:flex-col w">
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-neutral-200 bg-white px-8 pb-4">
                  <div className="flex py-6 mt-1 shrink-0 items-center">
                    <CaraWordMark scale={0.5} />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2">
                          {navigationGroups.map((group) => (
                            <>
                              {group.name && (
                                <li
                                  key={group.name}
                                  className="text-xs uppercase font-semibold leading-6 text-neutral-400 mt-6 mx-4 -mb-0.5 first:mt-0"
                                >
                                  {group.name}
                                </li>
                              )}
                              {group.pages.map((page) => (
                                <li key={page.name} className="mt-1.5 first:mt-0">
                                  <NavLink
                                    to={page.href}
                                    className={({ isActive }) =>
                                      clsx(
                                        isActive
                                          ? 'bg-primary-50 text-primary-600'
                                          : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary-600',
                                        'group flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6'
                                      )
                                    }
                                  >
                                    {({ isActive }) => (
                                      <>
                                        <page.icon
                                          aria-hidden="true"
                                          className={clsx(
                                            isActive
                                              ? 'text-primary-600'
                                              : 'text-neutral-400 group-hover:text-primary-600',
                                            'h-6 w-6 shrink-0'
                                          )}
                                        />
                                        {page.name}
                                      </>
                                    )}
                                  </NavLink>
                                </li>
                              ))}
                            </>
                          ))}
                        </ul>
                      </li>
                      <li className="mt-auto hidden">
                        <a
                          href="#"
                          className="group -mx-2 flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6 text-neutral-700 hover:bg-neutral-50 hover:text-primary-600"
                        >
                          <Cog6ToothIcon
                            aria-hidden="true"
                            className="h-6 w-6 shrink-0 text-neutral-400 group-hover:text-primary-600"
                          />
                          Settings
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

              <div className="lg:pl-64">
                <div className="sticky top-0 z-10 flex lg:hidden h-16 shrink-0 items-center gap-x-4 border-b border-neutral-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="-m-2.5 p-2.5 text-neutral-700 lg:hidden"
                  >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon aria-hidden="true" className="h-6 w-6" />
                  </button>

                  {/* Separator */}
                  <div aria-hidden="true" className="h-6 w-px bg-neutral-200 lg:hidden" />

                  <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                    <form action="#" method="GET" className="relative flex flex-1 opacity-0">
                      <label htmlFor="search-field" className="sr-only">
                        Search
                      </label>
                      <MagnifyingGlassIcon
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-neutral-400"
                      />
                      <input
                        id="search-field"
                        name="search"
                        type="search"
                        placeholder="Search..."
                        className="block h-full w-full border-0 py-0 pl-8 pr-0 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm"
                      />
                    </form>
                    <div className="flex items-center gap-x-4 lg:gap-x-6">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5 text-neutral-400 hover:text-neutral-500 hidden"
                      >
                        <span className="sr-only">View notifications</span>
                        <BellIcon aria-hidden="true" className="h-6 w-6" />
                      </button>

                      {/* Separator */}
                      <div
                        aria-hidden="true"
                        className="hidden lg:block lg:h-6 lg:w-px lg:bg-neutral-200"
                      />

                      {/* Profile dropdown */}
                      <Menu as="div" className="relative">
                        <MenuButton className="-m-1.5 flex items-center p-1.5">
                          <span className="sr-only">Open user menu</span>

                          <div className="rounded-full h-10 w-10 bg-primary-200/50 flex items-center justify-center font-medium text-sm">
                            {agencyUser.name.split(' ')[0].charAt(0)}
                            {agencyUser.name.split(' ')[1]?.charAt(0) || ''}
                          </div>
                          <span className="hidden lg:flex lg:items-center">
                            <span
                              aria-hidden="true"
                              className="ml-2 text-sm font-semibold leading-6 text-neutral-900"
                            >
                              {agencyUser.name}
                            </span>
                            {/* <ChevronDownIcon
                        aria-hidden="true"
                        className="ml-2 h-5 w-5 text-neutral-400"
                      /> */}
                          </span>
                        </MenuButton>
                      </Menu>
                    </div>
                  </div>
                </div>

                <main className="py-8 sm:py-12 bg-neutral-50 min-h-lvh">
                  <div className="px-8 sm:px-12">
                    <Outlet />
                  </div>
                </main>
              </div>
            </>
          );
        }}
      </Await>
    </React.Suspense>
  );
};

const DashboardContainerLoader = () => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <>
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-neutral-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            {/* Sidebar component, swap this element with another sidebar if you like */}
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-8 pb-4">
              <div className="flex py-6 mt-1 shrink-0 items-center">
                <div className="h-[30px] w-full bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="-mx-2">
                      {navigationGroups.map((group) => (
                        <>
                          {group.name && (
                            <li
                              key={group.name}
                              className="text-xs uppercase font-semibold leading-6 text-neutral-400 mt-6 mx-4 -mb-0.5 first:mt-0"
                            >
                              {group.name}
                            </li>
                          )}
                          {group.pages.map((page) => (
                            <li key={page.name} className="mt-1.5 first:mt-0">
                              <NavLink
                                to={page.href}
                                className={({ isActive }) =>
                                  clsx(
                                    isActive
                                      ? 'bg-primary-50 text-primary-600'
                                      : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary-600',
                                    'group flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6'
                                  )
                                }
                              >
                                {({ isActive }) => (
                                  <>
                                    <page.icon
                                      aria-hidden="true"
                                      className={clsx(
                                        isActive
                                          ? 'text-primary-600'
                                          : 'text-neutral-400 group-hover:text-primary-600',
                                        'h-6 w-6 shrink-0'
                                      )}
                                    />
                                    {page.name}
                                  </>
                                )}
                              </NavLink>
                            </li>
                          ))}
                        </>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto hidden">
                    <NavLink
                      to="#"
                      className="group -mx-2 flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6 text-neutral-700 hover:bg-neutral-50 hover:text-primary-600"
                    >
                      <Cog6ToothIcon
                        aria-hidden="true"
                        className="h-6 w-6 shrink-0 text-neutral-400 group-hover:text-primary-600"
                      />
                      Settings
                    </NavLink>
                  </li>
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-10 lg:flex lg:w-64 lg:flex-col w">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-neutral-200 bg-white px-8 pb-4">
          <div className="flex py-6 -mx-2 shrink-0 items-center">
            <div className="h-[30px] w-full bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
          </div>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2">
                  {navigationGroups.map((group) => (
                    <>
                      {group.name && (
                        <li
                          key={group.name}
                          className="text-xs uppercase font-semibold leading-6 text-neutral-400 mt-6 mx-4 -mb-0.5 first:mt-0"
                        >
                          {group.name}
                        </li>
                      )}
                      {group.pages.map((page) => (
                        <li key={page.name} className="mt-1.5 first:mt-0">
                          <NavLink
                            to={page.href}
                            className={({ isActive }) =>
                              clsx(
                                isActive
                                  ? 'bg-primary-50 text-primary-600'
                                  : 'text-neutral-700 hover:bg-neutral-50 hover:text-primary-600',
                                'group flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6'
                              )
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <page.icon
                                  aria-hidden="true"
                                  className={clsx(
                                    isActive
                                      ? 'text-primary-600'
                                      : 'text-neutral-400 group-hover:text-primary-600',
                                    'h-6 w-6 shrink-0'
                                  )}
                                />
                                {page.name}
                              </>
                            )}
                          </NavLink>
                        </li>
                      ))}
                    </>
                  ))}
                </ul>
              </li>
              <li className="mt-auto hidden">
                <a
                  href="#"
                  className="group -mx-2 flex gap-x-3 rounded-md py-2 px-4 text-sm font-semibold leading-6 text-neutral-700 hover:bg-neutral-50 hover:text-primary-600"
                >
                  <Cog6ToothIcon
                    aria-hidden="true"
                    className="h-6 w-6 shrink-0 text-neutral-400 group-hover:text-primary-600"
                  />
                  Settings
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <div className="lg:pl-64">
        <div className="sticky top-0 z-10 flex lg:hidden h-16 shrink-0 items-center gap-x-4 border-b border-neutral-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-neutral-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>

          {/* Separator */}
          <div aria-hidden="true" className="h-6 w-px bg-neutral-200 lg:hidden" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Profile dropdown */}
              <Menu as="div" className="relative">
                <MenuButton className="-m-1.5 flex items-center p-1.5">
                  <div className="rounded-full h-10 w-10 bg-primary-200/50 flex items-center justify-center font-medium text-sm"></div>
                  <span className="hidden lg:flex lg:items-center">
                    <div className="h-4 w-24 ml-2 bg-neutral-300 rounded-lg dark:bg-neutral-700 animate-pulse" />
                  </span>
                </MenuButton>
              </Menu>
            </div>
          </div>
        </div>

        <main className="py-8 sm:py-12 bg-neutral-50 min-h-lvh">
          <div className="px-8 sm:px-12"></div>
        </main>
      </div>
    </>
  );
};
